import React, { useState, useEffect, useMemo, useCallback } from "react";
import { createRoot } from "react-dom/client";
import {
  LanguageToggle, ThemeToggle, FilterBar, YearSection, EmptyState,
} from "./components.jsx";
import { Logo, GitHub } from "./icons.jsx";
import { UI_STRINGS, applyFilters, groupByYear, collectTags } from "./lib.jsx";
import "./styles.css";

function App() {
  // ─── Theme + lang (persisted) ───
  const [theme, setTheme] = useState(() => {
    try { return localStorage.getItem("dot.theme") || "dark"; } catch { return "dark"; }
  });
  const [lang, setLang] = useState(() => {
    try { return localStorage.getItem("dot.lang") || "en"; } catch { return "en"; }
  });
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try { localStorage.setItem("dot.theme", theme); } catch {}
  }, [theme]);
  useEffect(() => {
    document.documentElement.lang = lang;
    try { localStorage.setItem("dot.lang", lang); } catch {}
  }, [lang]);

  // ─── Data ───
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const r = await fetch("data/timeline.json", { cache: "no-store" });
        if (r.ok) {
          const j = await r.json();
          if (!cancelled) setData(j);
          return;
        }
      } catch {}
      if (!cancelled) setError("No timeline data available");
    }
    load();
    return () => { cancelled = true; };
  }, []);

  // ─── Filter state ───
  const [categories, setCategories] = useState(() => new Set());
  const [tags, setTags] = useState(() => new Set());
  const [query, setQuery] = useState("");

  const toggleCategory = useCallback((c) => {
    setCategories((prev) => {
      const n = new Set(prev);
      n.has(c) ? n.delete(c) : n.add(c);
      return n;
    });
  }, []);
  const resetCategories = useCallback(() => setCategories(new Set()), []);
  const toggleTag = useCallback((t) => {
    setTags((prev) => {
      const n = new Set(prev);
      n.has(t) ? n.delete(t) : n.add(t);
      return n;
    });
  }, []);
  const clearAll = useCallback(() => {
    setCategories(new Set());
    setTags(new Set());
    setQuery("");
  }, []);

  // ─── Derived ───
  const events = data?.events ?? [];
  const allTags = useMemo(() => collectTags(events), [events]);
  const filtered = useMemo(
    () => applyFilters(events, { categories, tags, query, lang }),
    [events, categories, tags, query, lang]
  );
  const groups = useMemo(() => groupByYear(filtered), [filtered]);

  const t = UI_STRINGS[lang];

  return (
    <div className="app">
      <div className="bg-grid" aria-hidden="true" />

      <header className="site-header">
        <div className="site-header-inner">
          <div>
            <h1>
              <span className="logo"><Logo /></span>
              {t.title}
            </h1>
            <p className="subtitle">{t.subtitle}</p>
            {data?.last_updated && (
              <div className="meta">
                {t.lastUpdated} · {data.last_updated} · schema v{data.schema_version}
              </div>
            )}
          </div>
          <div className="header-actions">
            <LanguageToggle lang={lang} onChange={setLang} />
            <ThemeToggle theme={theme} onChange={setTheme} />
          </div>
        </div>
      </header>

      {data && (
        <FilterBar
          lang={lang}
          categories={categories}
          onToggleCategory={toggleCategory}
          onResetCategories={resetCategories}
          tags={tags}
          onToggleTag={toggleTag}
          allTags={allTags}
          query={query}
          onQuery={setQuery}
          total={events.length}
          shown={filtered.length}
          onClearAll={clearAll}
        />
      )}

      <main className="timeline">
        {!data && !error && (
          <div className="loading">
            <span className="spin" />
            Loading timeline…
          </div>
        )}
        {error && (
          <div className="loading" role="alert">Failed to load timeline: {error}</div>
        )}
        {data && filtered.length === 0 && (
          <EmptyState lang={lang} onClear={clearAll} />
        )}
        {data && groups.map(({ year, items }) => (
          <YearSection
            key={year}
            year={year}
            items={items}
            lang={lang}
            onToggleTag={toggleTag}
            activeTags={tags}
          />
        ))}
      </main>

      <footer className="site-footer">
        <div className="site-footer-inner">
          <a href="https://github.com/krasina15/devopstimeline" target="_blank" rel="noreferrer noopener">
            <GitHub />
            {t.contribute}
          </a>
          <span>{t.builtWith} · {new Date().getFullYear()}</span>
        </div>
      </footer>
    </div>
  );
}

const root = createRoot(document.getElementById("root"));
root.render(<App />);
